import React from 'react'
import Typography, { TypographyProps } from '@material-ui/core/Typography'
import { useTheme } from '@material-ui/core/styles'

type Variant =
  | TypographyProps['variant']
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h3Grey'
  | 'h4'
  | 'h4Blue'
  | 'h4Red'
  | 'h4Primary'
  | 'h5'
  | 'h5Grey'
  | 'h5DarkGrey'
  | 'h5Blue'
  | 'h6'
  | 'h6Grey'
  | 'h6DarkGrey'
  | 'h6LightGrey'
  | 'h6Red'
  | 'h6Green'
  | 'subtitle1'
  | 'subtitle1Grey'
  | 'subtitle1LightGrey'
  | 'subtitle1DarkGrey'
  | 'subtitle1White'
  | 'subtitle2'
  | 'subtitle2DarkGrey'
  | 'body1'
  | 'body1Black'
  | 'body1Red'
  | 'body2'
  | 'body2DarkGrey'
  | 'body2LightGrey'
  | 'body2Blue'
  | 'caption'
  | 'captionGrey'
  | 'captionDarkGrey'
  | 'captionLightGrey'
  | 'captionRed'
  //ToDo: Caption2
  | 'buttonGrey'
  | 'buttonWhite'
  | 'buttonDarkGrey'
  | 'buttonLightGrey'
  | 'buttonBlue'
  //ToDo: Button2, Button3
  | 'overline'
  | 'overlineDarkGrey'
  | 'overlineLightGrey'

interface ITypographyProps {
  variant: Variant
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify'
  display?: 'initial' | 'block' | 'inline'
  gutterBottom?: boolean
  noWrap?: boolean
  paragraph?: boolean
  className?: string
  onClick?: (...args: any) => void
}

export const CentricTypography: React.FC<ITypographyProps> = props => {
  const theme = useTheme()
  const variantMapping = {
    h1: {
      variant: 'h1',
      color: theme.palette.text.secondary
    },
    h2: {
      variant: 'h2',
      color: theme.palette.text.secondary
    },
    h3: {
      variant: 'h3',
      color: theme.palette.text.secondary
    },
    h3Grey: {
      variant: 'h3',
      color: theme.palette.text.hint
    },
    h4: {
      variant: 'h4',
      color: theme.palette.text.secondary
    },
    h4Primary: {
      variant: 'h4',
      color: theme.palette.text.primary
    },
    h4Blue: {
      variant: 'h4',
      color: theme.palette.info.main
    },
    h4Red: {
      variant: 'h4',
      color: theme.palette.error.main
    },
    h5: {
      variant: 'h5',
      color: theme.palette.text.secondary
    },
    h5Grey: {
      variant: 'h5',
      color: theme.palette.text.hint
    },
    h5DarkGrey: {
      variant: 'h5',
      color: theme.palette.text.primary
    },
    h5Blue: {
      variant: 'h5',
      color: theme.palette.info.main
    },
    h6: {
      variant: 'h6',
      color: theme.palette.text.secondary
    },
    h6Grey: {
      variant: 'h6',
      color: theme.palette.text.hint
    },
    h6DarkGrey: {
      variant: 'h6',
      color: theme.palette.text.primary
    },
    h6LightGrey: {
      variant: 'h6',
      color: theme.palette.text.disabled
    },
    h6Red: {
      variant: 'h6',
      color: theme.palette.error.main
    },
    h6Green: {
      variant: 'h6',
      color: theme.palette.success.main
    },
    subtitle1: {
      variant: 'subtitle1',
      color: theme.palette.text.secondary
    },
    subtitle1Grey: {
      variant: 'subtitle1',
      color: theme.palette.text.hint
    },
    subtitle1LightGrey: {
      variant: 'subtitle1',
      color: theme.palette.text.disabled
    },
    subtitle1DarkGrey: {
      variant: 'subtitle1',
      color: theme.palette.text.primary
    },
    subtitle1White: {
      variant: 'subtitle1',
      color: theme.palette.primary.contrastText
    },
    subtitle2: {
      variant: 'subtitle2',
      color: theme.palette.text.primary
    },
    subtitle2DarkGrey: {
      variant: 'subtitle2',
      color: theme.palette.text.hint
    },
    body1: {
      variant: 'body1',
      color: theme.palette.text.primary
    },
    body1Black: {
      variant: 'body1',
      color: theme.palette.text.secondary
    },
    body1Red: {
      variant: 'body1',
      color: theme.palette.error.main
    },
    body2: {
      variant: 'body2',
      color: theme.palette.text.hint
    },
    body2DarkGrey: {
      variant: 'body2',
      color: theme.palette.text.primary
    },
    body2LightGrey: {
      variant: 'body2',
      color: theme.palette.text.disabled
    },
    body2Blue: {
      variant: 'body2',
      color: theme.palette.info.main
    },
    caption: {
      variant: 'caption',
      color: theme.palette.text.secondary
    },
    captionGrey: {
      variant: 'caption',
      color: theme.palette.text.hint
    },
    captionDarkGrey: {
      variant: 'caption',
      color: theme.palette.text.primary
    },
    captionLightGrey: {
      variant: 'caption',
      color: theme.palette.text.disabled
    },
    captionRed: {
      variant: 'caption',
      color: theme.palette.error.main
    },
    //ToDo: Caption2
    overline: {
      variant: 'overline',
      color: theme.palette.text.hint
    },
    overlineDarkGrey: {
      variant: 'overline',
      color: theme.palette.text.primary
    },
    overlineLightGrey: {
      variant: 'overline',
      color: theme.palette.text.disabled
    },
    buttonWhite: {
      variant: 'button',
      color: theme.palette.primary.contrastText
    },
    buttonGrey: {
      variant: 'button',
      color: theme.palette.text.hint
    },
    buttonDarkGrey: {
      variant: 'button',
      color: theme.palette.text.primary
    },
    buttonLightGrey: {
      variant: 'button',
      color: theme.palette.text.disabled
    },
    buttonBlue: {
      variant: 'button',
      color: theme.palette.info.main
    }
    //ToDo: Button2, Button3
  }

  const { variant, children } = props
  const mappedVariant = variantMapping[variant]
  return (
    <Typography
      {...props}
      data-test-id={`centricTypography-${variant}`}
      variant={mappedVariant.variant}
      style={{ color: mappedVariant.color }}
    >
      {children}
    </Typography>
  )
}
